import { onBeforeMount, computed, defineComponent, onMounted, reactive, ref, useContext, useCssModule, watch, } from '@nuxtjs/composition-api';
import { useTask } from 'vue-concurrency';
import { TABS } from '.';
import { RATINGS_DEFAULT_YEAR, RATINGS_DROPDOWN_YEARS } from './';
import { useDuck } from '~/services';
import { formatArticle } from '~/utils';
/**
 * Duck service.
 */
const duck = useDuck();
export default defineComponent({
    name: 'ArticleRatingWrapper',
    props: {
        article: {
            default: () => ({}),
            type: Object,
        },
    },
    setup(props) {
        /**
         * CSS Composables.
         */
        const context = useContext();
        const css = useCssModule();
        const viewport = context.$viewport;
        const currentArticle = ref(props.article);
        const title = ref(props.article.title);
        const ratingTrId = props.article?.rating?.trid;
        const dropdownYears = ref([]);
        /**
         * Actions
         */
        const actions = reactive({
            actions: computed(() => {
                const tooltipJustify = viewport.isLessThan('tablet') ? 'end' : 'center';
                return [
                    {
                        share: {
                            tooltipJustify,
                        },
                    },
                    {
                        copy: {
                            withText: false,
                        },
                    },
                    // 'bookmark',
                ];
            }),
            theme: computed(() => (viewport.isLessThan('tablet') ? 'dark' : 'light')),
            withShadow: false,
        });
        /**
         * Tabs
         */
        const tabs = reactive({
            currentTab: TABS[0],
            tabs: computed(() => {
                if (props.article.id) {
                    return TABS.map((tab) => {
                        tab.theme = 'lighter';
                        if (tab.value === tabs.currentTab.value) {
                            tab.theme = 'yellow';
                        }
                        return tab;
                    });
                }
                return [];
            }),
            change(tab) {
                const { title, value, theme = '' } = tab;
                tabs.currentTab = {
                    title,
                    theme,
                    value,
                };
            },
        });
        const isGrouped = computed(() => (currentArticle?.value?.rating || {}).grouped);
        const isInset = computed(() => (currentArticle?.value?.rating || {}).isInset);
        const currentTabComponent = computed(() => {
            if (tabs.currentTab.value === 'about') {
                return () => import('~/components/ArticleRatingAbout/ArticleRatingAbout.vue');
            }
            if (isGrouped.value) {
                return () => import('~/components/ArticleRatingGroups/ArticleRatingGroups.vue');
            }
            return () => import('~/components/ArticleRating/ArticleRating.vue');
        });
        /**
         * Year Dropdown
         */
        const dropdownYear = reactive({
            input(value) {
                dropdownYear.value = String(value);
                fetchRatingTask.perform();
            },
            isScrollable: true,
            items: computed(() => (dropdownYears.value.length ? dropdownYears.value : RATINGS_DROPDOWN_YEARS)),
            title: '',
            theme: computed(() => {
                return viewport.isLessThan('tablet') && !isInset.value ? 'dark' : 'light';
            }),
            value: props.article?.rating?.year || String(RATINGS_DEFAULT_YEAR),
            'reset-value': props.article?.rating?.year,
            'is-clearable': computed(() => props.article?.rating?.year !== dropdownYear.value),
        });
        /**
         * Search
         */
        const search = reactive({
            isOpened: false,
            isVisible: computed(() => (viewport.isLessThan('tablet') ? search.isOpened : true)),
            icon: computed(() => (search.isOpened ? 'close' : 'search')),
            timeout: 0,
            handleInput(event) {
                window.clearTimeout(search.timeout);
                search.timeout = window.setTimeout(() => {
                    search.query = event.target.value.trim();
                }, 500);
            },
            handleToggle() {
                search.isOpened = !search.isOpened;
            },
            query: '',
        });
        const isDone = ref(false);
        const fetchRatingTask = useTask(function* (signal) {
            signal.addEventListener('abort', () => duck.abortRequest('getPubRatings'));
            try {
                isDone.value = false;
                const { data } = yield duck.pub.getPubRatings({
                    'rating[trid]': ratingTrId,
                    'rating[year]': Number(dropdownYear.value),
                }, {
                    cancelToken: 'getPubRatings',
                });
                if (!data?.length && !data?.[0]?.article?.url_alias) {
                    isDone.value = true;
                    currentArticle.value = null;
                    title.value = 'Рейтинг не найден';
                    return;
                }
                let { data: article } = yield duck.pub.getPubArticleByAlias({
                    url_alias: data[0].article.url_alias,
                });
                article = formatArticle(article);
                title.value = article.title;
                currentArticle.value = {
                    ...article,
                    rating: {
                        ...article.rating,
                        ...(currentArticle.value?.rating?.isInset && {
                            isInset: true,
                        }),
                    },
                };
                isDone.value = true;
            }
            catch (error) {
                console.error(error);
                isDone.value = true;
            }
        });
        onBeforeMount(async () => {
            try {
                const { data } = await duck.pub.getPubRatings({
                    'rating[trid]': ratingTrId,
                    'list[limit]': 50,
                });
                dropdownYears.value = data.reduce((years, item) => {
                    if (!years.find(({ title }) => title === item.year)) {
                        years.push({
                            title: item.year,
                            value: item.year,
                        });
                    }
                    return years;
                }, []);
            }
            catch (error) { }
        });
        onMounted(() => {
            watch(() => search.query, (query) => {
                if (query.length > 0) {
                    const { title = '', value = '', theme = '' } = tabs.tabs.find(({ value }) => value === 'rating') || {};
                    tabs.currentTab = {
                        title,
                        theme,
                        value,
                    };
                }
            });
        });
        return {
            actions,
            css,
            currentArticle,
            currentTabComponent,
            dropdownYear,
            dropdownYears,
            fetchRatingTask,
            isDone,
            isGrouped,
            isInset,
            search,
            tabs,
            title,
        };
    },
});
